import Api from 'utils/api'

const FIELD_LABELS = {
  address: 'Street Address',
  address_2: 'Apartment, etc.',
  billing_address: 'Street Address',
  billing_address_2: 'Apartment, etc.',
  billing_city: 'City',
  billing_company_name: 'Company Name',
  billing_country: 'Country',
  billing_email: 'Billing Email',
  billing_name: 'Name on Card',
  billing_phone: 'Phone',
  billing_state: 'State',
  billing_zip: 'ZIP',
  card_number: 'Card number',
  card_type: 'Card type',
  city: 'City',
  company_name: 'Company Name',
  country: 'Country',
  creditcard: 'Credit card',
  cvv: 'CVV',
  email: 'Billing Email',
  expiration_month: 'Expiration Month',
  expiration_year: 'Expiration Year',
  name: 'Name on Card',
  phone_number: 'Phone',
  state: 'State',
  zip: 'ZIP',
}

const FIELD_ERROR_NAMES = {
  address: ['address', 'billing_address'],
  address_2: ['address_2', 'address2', 'billing_address_2'],
  card_number: ['card_number'],
  card_type: ['card_type', 'cardType'],
  city: ['city', 'billing_city'],
  company_name: ['company_name', 'companyName', 'billing_company_name'],
  country: ['country', 'billing_country'],
  cvv: ['cvv', 'cvc'],
  email: ['email', 'billing_email'],
  expiration_month: ['expiration_month', 'expirationMonth'],
  expiration_year: ['expiration_year', 'expirationYear'],
  name: ['name', 'billing_name', 'contact_name'],
  phone_number: ['phone_number', 'phoneNumber', 'phone', 'billing_phone'],
  state: ['state', 'billing_state'],
  zip: ['zip', 'billing_zip'],
}

const CREDIT_CARD_ERROR_FIELD_PATTERNS = [
  { fieldName: 'name', pattern: /\b(?:cardholder|card holder|first name|last name|name)\b/i },
  { fieldName: 'cvv', pattern: /\b(?:cvv|cvc|cid|security code|verification code|verification value|card verification)\b/i },
  { fieldName: 'card_type', pattern: /\b(?:card type|credit card type|type)\b/i },
  { fieldName: 'expiration_month', pattern: /\b(?:expiration month|exp month)\b/i },
  { fieldName: 'expiration_year', pattern: /\b(?:expiration year|exp year)\b/i },
  { fieldName: 'expiration_month', pattern: /\b(?:expiration|expired|expiry|exp date)\b/i },
  { fieldName: 'zip', pattern: /\b(?:zip|postal)\b/i },
  { fieldName: 'address', pattern: /\b(?:address|street)\b/i },
  { fieldName: 'country', pattern: /\bcountry\b/i },
  { fieldName: 'state', pattern: /\bstate\b/i },
  { fieldName: 'city', pattern: /\bcity\b/i },
  { fieldName: 'email', pattern: /\bemail\b/i },
  { fieldName: 'phone_number', pattern: /\bphone\b/i },
  { fieldName: 'card_number', pattern: /\b(?:number|card number|pan)\b/i },
]

const FALLBACK_CARD_TYPE_OPTIONS = [
  { label: 'VISA', value: 'visa' },
  { label: 'MasterCard', value: 'master' },
  { label: 'American Express', value: 'american_express' },
  { label: 'Discover', value: 'discover' },
]

const FALLBACK_COUNTRY_OPTIONS = [
  { label: '', value: '' },
  { label: 'United States (US)', value: 'US' },
]

const FALLBACK_MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
].map((month, index) => ({
  label: `${String(index + 1).padStart(2, '0')} - ${month}`,
  value: String(index + 1),
}))

const fallbackYearOptions = () => {
  const year = new Date().getFullYear()
  return Array.from({ length: 10 }, (_value, index) => ({
    label: String(year + index),
    value: String(year + index),
  }))
}

export const formatCurrency = (value, currency = 'USD') => {
  if (value === null || value === undefined || value === '') return 'None'
  const amount = Number(value)
  if (Number.isNaN(amount)) return value

  const formatted = new Intl.NumberFormat('en-US', { currency, style: 'currency' }).format(Math.abs(amount))
  return amount < 0 ? `(${formatted})` : formatted
}

export const formatDate = value => {
  if (!value) return 'None'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'None'
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric', month: 'short', timeZone: 'UTC', year: 'numeric',
  }).format(date)
}

export const formatMonthDay = value => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric', month: 'short', timeZone: 'UTC',
  }).format(date)
}

export const formatYear = value => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.getUTCFullYear()
}

export const titleize = value => {
  const text = String(value || 'None').replaceAll('_', ' ')
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`
}

const normalizeErrorMessages = messages => (
  Array.isArray(messages) ? messages : [messages]
).filter(Boolean).map(String)

const validationLabelForField = fieldName => FIELD_LABELS[fieldName] ||
  String(fieldName).replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase())

const validationSummaryMessageForField = (fieldName, message) => {
  const prefixPatterns = {
    card_number: /^(?:credit card number|card number|number)\s+/i,
    cvv: /^(?:cvv|cvc|cid|security code|verification code|verification value|card verification value)\s+/i,
    expiration_month: /^expiration month\s+/i,
    expiration_year: /^expiration year\s+/i,
    name: /^(?:name on card|cardholder name|card holder name|name)\s+/i,
  }

  return message.replace(prefixPatterns[fieldName] || /^$/, '')
}

export const validationSummaryMessage = validationErrors => Object.entries(validationErrors || {})
  .flatMap(([fieldName, messages]) => normalizeErrorMessages(messages).map(message => ({ fieldName, message })))
  .map(({ fieldName, message }) => fieldName === 'base'
    ? message
    : `${validationLabelForField(fieldName)} ${validationSummaryMessageForField(fieldName, message)}`)
  .join('; ')

export const validationMessagesForField = (validationErrors, fieldName) => {
  const errorNames = FIELD_ERROR_NAMES[fieldName] || [fieldName]
  return errorNames.flatMap(errorName => normalizeErrorMessages(validationErrors?.[errorName]))
}

const appendValidationError = (validationErrors, fieldName, message) => {
  validationErrors[fieldName] = [...normalizeErrorMessages(validationErrors[fieldName]), message]
}

const normalizeValidationErrors = validationErrors => {
  const normalizedErrors = {}

  Object.entries(validationErrors || {}).forEach(([fieldName, messages]) => {
    normalizeErrorMessages(messages).forEach(message => {
      if (fieldName !== 'creditcard') {
        appendValidationError(normalizedErrors, fieldName, message)
        return
      }

      message
        .replace(/^\s*credit\s*card\s+/i, '')
        .replace(/^\s*could not validate credit card:\s*/i, '')
        .trim()
        .split(/\s*,\s*/)
        .map(errorMessage => errorMessage.trim().replace(/[.;]\s*$/, ''))
        .filter(Boolean)
        .forEach(creditCardMessage => {
          const normalizedFieldName = CREDIT_CARD_ERROR_FIELD_PATTERNS
            .find(({ pattern }) => pattern.test(creditCardMessage))?.fieldName || 'creditcard'

          appendValidationError(normalizedErrors, normalizedFieldName, creditCardMessage)
        })
    })
  })

  return normalizedErrors
}

export const requestBillingApi = async (method, endpoint, body) => {
  let failureResponse = null
  try {
    return await Api[method](endpoint, body, {
      onFailure: (_response, json) => { failureResponse = json },
    })
  } catch (error) {
    error.validationErrors = normalizeValidationErrors(failureResponse?.model_errors || failureResponse?.errors)
    throw error
  }
}

export const accountPayloadFromForm = form => ({
  address: form.get('address') || '',
  address_2: form.get('address_2') || '',
  card_number: form.get('card_number') || '',
  card_type: form.get('card_type') || '',
  city: form.get('city') || '',
  company_name: form.get('company_name') || '',
  country: form.get('country') || '',
  cvv: form.get('cvv') || '',
  email: form.get('email') || '',
  expiration_month: form.get('expiration_month') || '',
  expiration_year: form.get('expiration_year') || '',
  name: form.get('name') || '',
  phone_number: form.get('phone_number') || '',
  state: form.get('state') || '',
  zip: form.get('zip') || '',
})

export const clearCardFields = form => ['card_number', 'cvv'].forEach(name => {
  if (form.elements[name]) form.elements[name].value = ''
})

export const optionList = options => Array.isArray(options)
  ? options.map(option => ({
    label: String(option?.label ?? ''),
    value: String(option?.value ?? option?.label ?? ''),
  }))
  : []

const optionsOrFallback = (options, fallback) => {
  const normalizedOptions = optionList(options)
  return normalizedOptions.length > 0 ? normalizedOptions : fallback
}

export const billingFormOptions = formOptions => ({
  cardTypes: optionsOrFallback(formOptions?.cardTypes, FALLBACK_CARD_TYPE_OPTIONS),
  countries: optionsOrFallback(formOptions?.countries, FALLBACK_COUNTRY_OPTIONS),
  months: optionsOrFallback(formOptions?.months, FALLBACK_MONTH_OPTIONS),
  states: optionList(formOptions?.states),
  years: optionsOrFallback(formOptions?.years, fallbackYearOptions()),
})

const valueFor = (object, camelKey, snakeKey) => object?.[camelKey] ?? object?.[snakeKey]
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key)
const hasEitherKey = (object, camelKey, snakeKey) => hasOwn(object, camelKey) || hasOwn(object, snakeKey)
const normalizeBillingAccount = account => ({
  ...(account || {}),
  address2: valueFor(account, 'address2', 'address_2'),
  companyName: valueFor(account, 'companyName', 'company_name'),
  phoneNumber: valueFor(account, 'phoneNumber', 'phone_number'),
})

const normalizeBillingFormOptions = formOptions => ({
  ...(formOptions || {}),
  cardTypes: valueFor(formOptions, 'cardTypes', 'card_types'),
})

export const normalizeBillingHistory = history => Array.isArray(history)
  ? history.map(item => ({
    ...item,
    createdAt: valueFor(item, 'createdAt', 'created_at'),
    hasPdf: valueFor(item, 'hasPdf', 'has_pdf'),
  }))
  : []
export const normalizeInvoicePdf = pdf => ({
  ...(pdf || {}),
  contentBase64: valueFor(pdf, 'contentBase64', 'content_base64'),
  contentType: valueFor(pdf, 'contentType', 'content_type'),
})
const normalizePaymentMethod = paymentMethod => ({
  ...(paymentMethod || {}),
  billingEmail: valueFor(paymentMethod, 'billingEmail', 'billing_email'),
  cardNumber: valueFor(paymentMethod, 'cardNumber', 'card_number'),
  cardType: valueFor(paymentMethod, 'cardType', 'card_type'),
})

const normalizeSubscription = subscription => ({
  ...(subscription || {}),
  cancelAtRenewal: valueFor(subscription, 'cancelAtRenewal', 'cancel_at_renewal'),
  expirationDate: valueFor(subscription, 'expirationDate', 'expiration_date'),
  siteName: valueFor(subscription, 'siteName', 'site_name'),
})

export const mergeBillingSummary = (currentSummary, summaryUpdate) => ({
  ...(currentSummary || {}),
  ...summaryUpdate,
})

const hasUpgradeQuote = upgrade => Boolean(
  upgrade?.amount !== null && upgrade?.amount !== undefined && upgrade?.currency && upgrade?.interval,
)

export const normalizeBillingSummary = summary => {
  const responseUpgradeRequired = valueFor(summary, 'upgradeRequired', 'upgrade_required')
  return {
    ...summary,
    account: normalizeBillingAccount(summary?.account),
    ...(hasEitherKey(summary, 'accountExists', 'account_exists')
      ? { accountExists: valueFor(summary, 'accountExists', 'account_exists') }
      : {}),
    formOptions: normalizeBillingFormOptions(valueFor(summary, 'formOptions', 'form_options')),
    ...(summary && 'history' in summary ? { history: normalizeBillingHistory(summary.history) } : {}),
    ...(hasEitherKey(summary, 'paymentMethod', 'payment_method')
      ? { paymentMethod: normalizePaymentMethod(valueFor(summary, 'paymentMethod', 'payment_method')) }
      : {}),
    ...(hasOwn(summary, 'subscription') ? { subscription: normalizeSubscription(summary.subscription) } : {}),
    upgrade: summary?.upgrade || {},
    ...(hasEitherKey(summary, 'upgradeRequired', 'upgrade_required')
      ? { upgradeRequired: responseUpgradeRequired === true }
      : {}),
  }
}

export const billingSummaryUnavailable = summary => summary?.upgradeRequired && !hasUpgradeQuote(summary.upgrade)
export const shouldLoadBillingHistory = summary => summary.accountExists && !summary.upgradeRequired
